// The Run panel (docs/cockpit/spec.md): every step of a Run with who and when, and every artefact that went out
// or came in, each opened in one tap: a message opens in a panel with its words exactly as sent or received, a
// Document, photo, challan or receipt opens from the Application API. Call buttons for everyone involved LAMP
// has a phone for, the Credits each step and the Run used, and "Why?" on each entry answers from the rules,
// Playbook lines and approvals the Audit Log recorded. Nothing is edited from here.
import { useEffect, useId, useMemo, useState } from "react";
import {
  ApiError,
  rupees,
  type Api,
  type AuditEntry,
  type Costs,
  type OrderCost,
  type RunCredits,
  type RunPage,
} from "../api.js";
import { creditsOf } from "../credits/Credits.js";
import { Dialog } from "../Dialog.js";
import { formatDate, formatNumber } from "../i18n/format.js";
import type { Language } from "../i18n/language.js";
import { useLanguage, useWords, type Words } from "../i18n/words.js";
import { KeptFiles } from "../files/KeptFiles.js";
import { RunsList } from "./RunsList.js";
import { runPanel, telHref, type Artefact, type PanelEntry, type RunPanel as Panel } from "./panel.js";

const at = (iso: string, language: Language) => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : formatDate(date, language, { dateStyle: "medium", timeStyle: "short" });
};

const artefactLabels = (w: Words): Readonly<Record<Artefact["kind"], string>> => ({
  message: w.runPanelMessage,
  document: w.runPanelDocument,
  photo: w.runPanelPhoto,
  challan: w.runPanelChallan,
  receipt: w.runPanelReceipt,
  file: w.runPanelFile,
});

type Message = NonNullable<Artefact["message"]>;

function ArtefactLink({ artefact, onOpen }: { artefact: Artefact; onOpen: (message: Message) => void }) {
  const w = useWords();
  const label = `${artefactLabels(w)[artefact.kind]}: ${artefact.title}`;
  if (artefact.message) {
    const { message } = artefact;
    return (
      <button type="button" className="btn" onClick={() => onOpen(message)}>
        {label}
      </button>
    );
  }
  if (!artefact.href) return <span className="tag tag-plain">{label}</span>;
  return (
    <a className="btn" href={artefact.href} target="_blank" rel="noopener">
      {label}
    </a>
  );
}

function Entry({ entry, onOpen }: { entry: PanelEntry; onOpen: (message: Message) => void }) {
  const w = useWords();
  const { language } = useLanguage();
  const [asked, setAsked] = useState(false);
  const whyId = useId();
  return (
    <li className="run-entry">
      <p>
        <strong>{entry.what}</strong>
        <span className="muted">
          {" "}
          · {entry.who} · <time dateTime={entry.at}>{at(entry.at, language)}</time>
        </span>
      </p>
      {entry.artefacts.length > 0 ? (
        <ul className="artefacts" aria-label={w.runPanelArtefacts}>
          {entry.artefacts.map((a) => (
            <li key={a.key}>
              <ArtefactLink artefact={a} onOpen={onOpen} />
            </li>
          ))}
        </ul>
      ) : null}
      <button
        type="button"
        className="linkish"
        aria-expanded={asked}
        aria-controls={whyId}
        onClick={() => setAsked(!asked)}
      >
        {w.runPanelWhy}
      </button>
      <ul id={whyId} className="more" hidden={!asked}>
        {entry.why.length > 0 ? entry.why.map((line, i) => <li key={i}>{line}</li>) : <li>{w.runPanelNoWhy}</li>}
      </ul>
    </li>
  );
}

/**
 * The cost and margin of the order a Run is about, for a person whose Roles see costs and margins: estimated, so far
 * and where it is heading, by material, time and job work, and what cost more than planned. Nothing for anyone else.
 */
export function OrderCostView({ cost }: { cost: OrderCost }) {
  const w = useWords();
  const { language } = useLanguage();
  const pct = (value: number) => `${formatNumber(value, language, { maximumFractionDigits: 1 })}%`;
  const columns: readonly [string, Costs | undefined][] = [
    [w.runPanelCostEstimated, cost.estimated],
    [w.runPanelCostSoFar, cost.actual],
    [w.runPanelCostProjected, cost.projected],
  ];
  const rows: readonly [string, keyof Costs][] = [
    [w.runPanelCostMaterial, "material"],
    [w.runPanelCostTime, "conversion"],
    [w.runPanelCostJobWork, "job_work"],
    [w.runPanelCostTotal, "total"],
  ];
  return (
    <section className="section" aria-labelledby="cost-heading">
      <h2 id="cost-heading">{w.runPanelCostHeading}</h2>
      <p>{w.runPanelCostOrder(cost.number, cost.customer)}</p>
      <table className="tbl">
        <thead>
          <tr>
            <th scope="col">{w.runPanelCostCosts}</th>
            {columns.map(([label]) => (
              <th key={label} scope="col">
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, key]) => (
            <tr key={key}>
              <th scope="row">{label}</th>
              {columns.map(([column, costs]) => (
                <td key={column}>{costs ? rupees(costs[key]) : "—"}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {cost.quotedMarginPct !== undefined ? <p>{w.runPanelMarginQuoted(pct(cost.quotedMarginPct))}</p> : null}
      {cost.projectedMarginPct !== undefined ? (
        <p>
          <strong>{w.runPanelMarginProjected(pct(cost.projectedMarginPct))}</strong>
        </p>
      ) : null}
      {cost.atRisk ? (
        <p className="alert" role="status">
          {w.runPanelMarginAtRisk(cost.marginPoints)}
        </p>
      ) : null}
      <h3>{w.runPanelCostCauses}</h3>
      {cost.causes.length > 0 ? (
        <ul className="plain-list">
          {cost.causes.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
      ) : (
        <p className="muted">{w.runPanelCostNoCauses}</p>
      )}
    </section>
  );
}

/** The Run panel from its Audit Log entries; the Credits only for a person whose Roles read them (`credits`). */
export function RunPanelView({ panel, credits = true }: { panel: Panel; credits?: boolean }) {
  const w = useWords();
  const { language } = useLanguage();
  const [open, setOpen] = useState<Message | undefined>(undefined);
  const callable = panel.involved.flatMap((p) => (p.phone ? [{ name: p.name, phone: p.phone }] : []));
  const others = panel.involved.filter((p) => !p.phone);
  return (
    <>
      {credits ? (
        <p>
          {panel.about ?? w.runPanelThisRun} {w.runPanelUsedMiddle}{" "}
          <strong>{w.runPanelCredits(creditsOf(panel.milliCredits, language))}</strong>
          {w.runPanelUsedAfter}
          {panel.unplacedMilliCredits > 0 ? w.runPanelUnplaced(creditsOf(panel.unplacedMilliCredits, language)) : ""}
        </p>
      ) : null}
      {panel.involved.length > 0 ? (
        <section className="section" aria-labelledby="involved-heading">
          <h2 id="involved-heading">{w.runPanelInvolved}</h2>
          <div className="run-calls">
            {callable.map((p) => (
              <a key={p.name} className="btn" href={telHref(p.phone)}>
                {w.runPanelCall(p.name)}
              </a>
            ))}
          </div>
          {others.length > 0 ? (
            <p className="muted">{w.runPanelAlsoInvolved(others.map((p) => p.name).join(", "))}</p>
          ) : null}
        </section>
      ) : null}
      <section className="section" aria-labelledby="steps-heading">
        <h2 id="steps-heading">{w.runPanelSteps}</h2>
        {panel.steps.length === 0 ? (
          <p className="muted">{w.runPanelNoSteps}</p>
        ) : (
          <ol className="run-steps">
            {panel.steps.map((step) => (
              <li key={step.node ?? "run"} className="item">
                <h3>
                  {step.node === null ? w.runPanelTheRun : (step.name ?? w.runPanelStep(step.node))}
                  {credits ? (
                    <span className="muted"> · {w.runPanelCredits(creditsOf(step.milliCredits, language))}</span>
                  ) : null}
                </h3>
                <ul className="plain-list">
                  {step.entries.map((e) => (
                    <Entry key={e.sequence} entry={e} onOpen={setOpen} />
                  ))}
                </ul>
              </li>
            ))}
          </ol>
        )}
      </section>
      {open ? (
        <Dialog
          title={open.direction === "sent" ? w.runPanelMessageTo(open.party) : w.runPanelMessageFrom(open.party)}
          onClose={() => setOpen(undefined)}
        >
          <p className="muted">
            {open.direction === "sent" ? w.runPanelSentOn(open.channel) : w.runPanelReceivedOn(open.channel)}
          </p>
          <pre className="verbatim">{open.text}</pre>
        </Dialog>
      ) : null}
    </>
  );
}

function problemOf(error: unknown, w: Words): string {
  if (error instanceof ApiError && error.status === 403) return w.runPanelForbidden;
  if (error instanceof ApiError && (error.status === 400 || error.status === 404)) return w.runPanelNotARun;
  return w.runPanelFailed;
}

/**
 * Why the Run is stopped or on whom it waits, at the top of its page (#513), with the card to act on when it waits on
 * the person reading.
 */
export function RunStandingView({
  page,
  onOpenWorkItem,
}: {
  page: RunPage;
  onOpenWorkItem?: ((workItem: string) => void) | undefined;
}) {
  const w = useWords();
  const { standing } = page;
  if (!standing) return null;
  const task = standing.task ?? w.runStandingATask;
  const head = {
    blocked: w.runStandingBlocked(task),
    waiting: standing.prepared ? w.runStandingPrepared(task) : w.runStandingWaiting(task),
    paused: w.runStandingPaused,
    halted: w.runStandingHalted,
  }[standing.kind];
  const why = [
    ...(standing.pausedBecause ? [w.runStandingPausedBecause[standing.pausedBecause]] : []),
    ...standing.reasons,
  ];
  const whom = standing.yours
    ? w.runStandingOnYou
    : standing.onWhom === undefined
      ? undefined
      : "lamp" in standing.onWhom
        ? w.runStandingOnLamp
        : w.runStandingOn(standing.onWhom.role);
  const { workItem } = standing;
  return (
    <section
      className={standing.kind === "waiting" ? "standing" : "standing standing-stopped"}
      aria-label={w.runStanding}
    >
      <p>
        <strong>{head}</strong>
      </p>
      {why.length > 0 ? (
        <ul className="plain-list">
          {why.map((reason, i) => (
            <li key={i}>{reason}</li>
          ))}
        </ul>
      ) : null}
      {whom ? <p className="muted">{whom}</p> : null}
      {standing.yours && workItem !== undefined && onOpenWorkItem ? (
        <button type="button" className="btn btn-go" onClick={() => onOpenWorkItem(workItem)}>
          {w.runStandingOpen}
        </button>
      ) : null}
    </section>
  );
}

/** The Run's steps and their Work Items, for a person whose Roles do not read its Audit Log entries. */
export function RunStepsView({ page }: { page: RunPage }) {
  const w = useWords();
  return (
    <section className="section" aria-labelledby="steps-heading">
      <h2 id="steps-heading">{w.runPanelSteps}</h2>
      {page.steps.length === 0 ? (
        <p className="muted">{w.runPanelNoSteps}</p>
      ) : (
        <ol className="run-steps">
          {page.steps.map((step) => (
            <li key={step.node} className="item">
              <h3>{step.name}</h3>
              <ul className="plain-list">
                {step.workItems.map((item, i) => (
                  <li key={i}>
                    {item.name} <span className="muted">· {w.runStepStatus[item.status]}</span>
                    {item.note ? <p className="muted">{item.note}</p> : null}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

type Answer =
  | { readonly state: "loading" }
  | {
      readonly state: "ready";
      readonly page: RunPage;
      /** The Audit Log's entries and the Credits, when the person's Roles read them; left out otherwise. */
      readonly entries?: readonly AuditEntry[];
      readonly credits?: RunCredits;
      /** Whether a read the person's Roles allow failed, so the page shows what it has and says what it lacks. */
      readonly partly: boolean;
    }
  | { readonly state: "failed"; readonly error: unknown };

const NO_CREDITS: RunCredits = { run: "", milliCredits: 0, credits: 0, steps: 0, modelCalls: 0, byNode: {} };

/**
 * A Run's page for anyone who may see the Run (#513): what it is about and why it stands where it does, its order's
 * cost where it has one, and its steps; from the Audit Log, with the Credits, only for a person whose Roles read them.
 * Below them, the files kept against the Run (#575), and against its order for a person who may read it (#818).
 * Nothing is asked that would be refused, so the page never fails on what the person may not see; an Audit Log or
 * Credits read that fails leaves where the Run stands, its cost and its steps on the page (#673).
 */
export function RunPanel({
  api,
  run,
  onOpenWorkItem,
}: {
  api: Api;
  run: string;
  onOpenWorkItem?: ((workItem: string) => void) | undefined;
}) {
  const w = useWords();
  const [answer, setAnswer] = useState<Answer>({ state: "loading" });
  useEffect(() => {
    let live = true;
    setAnswer({ state: "loading" });
    (async () => {
      const page = await api.runPage(run);
      let partly = false;
      const failed = () => {
        partly = true;
        return undefined;
      };
      const [entries, credits] = await Promise.all([
        page.sees.audit ? api.runEntries(run).catch(failed) : undefined,
        page.sees.credits ? api.runCredits(run).catch(failed) : undefined,
      ]);
      return {
        state: "ready" as const,
        page,
        partly,
        ...(entries ? { entries } : {}),
        ...(credits ? { credits } : {}),
      };
    })().then(
      (ready) => {
        if (live) setAnswer(ready);
      },
      (error: unknown) => {
        if (live) setAnswer({ state: "failed", error });
      },
    );
    return () => {
      live = false;
    };
  }, [api, run]);
  // Worded here, not when loaded, so switching the language rewords the panel without loading it again.
  const panel = useMemo(
    () =>
      answer.state === "ready" && answer.entries
        ? runPanel(run, answer.entries, answer.credits ?? NO_CREDITS, w)
        : undefined,
    [answer, run, w],
  );
  if (answer.state === "failed") return <p role="alert">{problemOf(answer.error, w)}</p>;
  if (answer.state === "loading") return <p role="status">{w.runPanelLoading}</p>;
  const { page } = answer;
  const { keptFiles } = api;
  return (
    <>
      {page.subject ? <h2 className="run-subject">{page.subject}</h2> : null}
      <RunStandingView page={page} onOpenWorkItem={onOpenWorkItem} />
      {page.cost ? <OrderCostView cost={page.cost} /> : null}
      {answer.partly ? <p role="alert">{w.runPanelPartly}</p> : null}
      {panel ? <RunPanelView panel={panel} credits={answer.credits !== undefined} /> : <RunStepsView page={page} />}
      {keptFiles ? <KeptFiles api={{ keptFiles }} object="run" record={run} /> : null}
      {keptFiles && page.order ? (
        <KeptFiles api={{ keptFiles }} object={page.order.object} record={page.order.id} title={w.keptFilesOfOrder} />
      ) : null}
    </>
  );
}

const RUN_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The Run a path shows, `/runs/:run`, if any. */
export function runAt(path: string): string | undefined {
  const [, first, run] = path.split("/");
  return first === "runs" && run !== undefined && RUN_ID.test(run) ? run : undefined;
}

/** The Runs page: a Run's panel, or the list of Runs to open one from. */
export function Runs({
  api,
  run,
  initialQuery,
  onOpenRun,
  onOpenWorkItem,
}: {
  api: Api;
  run: string | undefined;
  initialQuery?: string;
  onOpenRun: (run: string) => void;
  /** Opens a Work Item's card, where the person acts on it. */
  onOpenWorkItem?: (workItem: string) => void;
}) {
  if (run) return <RunPanel api={api} run={run} onOpenWorkItem={onOpenWorkItem} />;
  return <RunsList api={api} onOpenRun={onOpenRun} {...(initialQuery ? { initialQuery } : {})} />;
}
